import { EyeIcon, EyeSlashIcon } from '@heroicons/react/20/solid'
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import { GET_USER_LIST } from 'graphql/users/queries'
import { t } from 'i18next'
import { ErrorDialog, SuccessDialog, ConfirmDialog } from 'components/custom/ResponseDialog'
import { Badge } from 'components/badge'
import { LANGUAGES } from 'pages/constants'
import { ImageDisplay } from 'components/custom/ImageDisplay'
import placeHolderImage from 'assets/images/users/profile_default.png'

// App Users module imports
import { APP_USER_POOL } from '../constants'
import { getUserFullName, getUserStatusBadgeColor, getUserStatusText } from '../helpers'
import { LoadingOverlay, BackButton, ErrorState, EmptyState } from '../components'
import useUserStatus from '../hooks/useUserStatus'

export default function AppUserDetails() {
    const { id } = useParams()
    const navigate = useNavigate()
    const [userList, setUserList] = useState(null)

    // Search users function
    const searchUsers = () => {
        getUserList({
            variables: {
                searchUserId: id,
                role: null,
                userPool: APP_USER_POOL,
                page: { offset: 0, limit: 1 },
                searchString: '',
            },
        })
    }

    // User status hook
    const {
        showErrDialog,
        showSucDialog,
        showConfirmDialog,
        errorMessage,
        successMessage,
        confirmMessage,
        statusUpdateLoading,
        handleDisableUser,
        handleConfirmPositive,
        handleConfirmNegative,
        handleErrClose,
        handleSucClose,
    } = useUserStatus(searchUsers)

    const [getUserList, { loading: userListLoading, error: userListError }] = useLazyQuery(GET_USER_LIST, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.searchUser.success && data.searchUser.code === '200') {
                setUserList(data.searchUser.users)
            }
        },
    })

    useEffect(() => {
        searchUsers()
    }, [id])

    const handleBack = () => navigate('/app-users')

    const user = userList?.[0]

    return (
        <>
            <div className="mb-6 flex justify-normal items-center">
                <BackButton onClick={handleBack} />
                <h3 className="text-lg font-bold leading-7 text-primary ps-2.5 pb-1">{t('User Details')}</h3>
            </div>
            {userListLoading && <div className="flex justify-center items-center bg-white py-5">{t('Loading...')}</div>}
            {userListError && <ErrorState message={t('Failed to fetch user details!')} />}
            {!userListLoading && !userListError && userList?.length === 0 && (
                <EmptyState title={t('No data found!')} message="" />
            )}
            {!userListLoading && !userListError && user && (
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div className="max-sm:w-full sm:flex-1">
                        <div className="md:flex md:items-center md:justify-between md:space-x-5">
                            <div className="flex items-start space-x-5">
                                <div className="flex-shrink-0">
                                    <div className="relative">
                                        <ImageDisplay
                                            s3Key={user.additional_info?.profile_photo?.s3_key}
                                            file=""
                                            placeHolderImage={placeHolderImage}
                                            classStyle="h-16 w-16 rounded-full"
                                        />
                                    </div>
                                </div>
                                <div className="pt-1.5">
                                    <h1 className="text-2xl font-bold text-gray-900">
                                        {getUserFullName(user)}
                                    </h1>
                                    <p className="text-sm font-medium text-gray-500">{user.email}</p>
                                </div>
                            </div>
                            <div className="mt-6 flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-3 sm:space-y-0 sm:space-x-reverse md:mt-0 md:flex-row md:space-x-3">
                                {/* Enable/Disable Button - Only action for app users */}
                                <button
                                    onClick={() => handleDisableUser(user.id, !user.is_active)}
                                    type="button"
                                    className={`inline-flex items-center justify-center gap-x-1.5 rounded-md px-3 py-2 text-sm font-semibold shadow-sm ${
                                        user.is_active
                                            ? 'bg-rose-600 text-white hover:bg-rose-500'
                                            : 'bg-primary text-white hover:bg-primary/[.9]'
                                    }`}
                                >
                                    {user.is_active ? (
                                        <EyeSlashIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                                    ) : (
                                        <EyeIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                                    )}
                                    {getUserStatusText(user.is_active) === t('Active') ? t('Disable') : t('Enable')}
                                </button>
                            </div>
                        </div>

                        <div className="mt-8">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary">
                                    {t('Personal Information')}
                                </h3>
                                <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
                                    {t('Personal Information of the user for contact purpose')}
                                </p>
                            </div>
                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">{t('First Name')}</dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {user.user_info?.given_name || '-'}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Last Name')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {user.user_info?.family_name || '-'}
                                        </dd>
                                    </div>
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Email Address')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {user.email || '-'}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Mobile Number')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {user.user_info?.phone_number || '-'}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>

                        <div className="mt-8">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary">{t('User Profile')}</h3>
                                <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
                                    {t('This is information which will define the access type and user access the system.')}
                                </p>
                            </div>
                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Status')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            <Badge color="lime">{user.cognito_status}</Badge>
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">{t('User Status')}</dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            <Badge color={getUserStatusBadgeColor(user.is_active)}>
                                                {getUserStatusText(user.is_active)}
                                            </Badge>
                                        </dd>
                                    </div>
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Preferred Language')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {user.user_info?.locale === 'ar' ? LANGUAGES.ar : LANGUAGES.en}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>

                        {/* Passport Details Section */}
                        {user.additional_info?.attachments?.find(a => a.document_type === 'PASSPORT') && (
                            <div className="mt-8">
                                <div className="px-4 sm:px-0">
                                    <h3 className="text-lg font-semibold leading-7 text-primary">
                                        {t('Passport Details')}
                                    </h3>
                                    <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
                                        {t('Passport information of the user')}
                                    </p>
                                </div>
                                <div className="mt-3 border-t border-gray-100">
                                    <dl className="divide-y divide-gray-100">
                                        <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {t('Passport Number')}
                                            </dt>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                {user.additional_info.attachments.find(a => a.document_type === 'PASSPORT')?.document_number || '-'}
                                            </dd>
                                        </div>
                                        <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {t('Passport Attachment')}
                                            </dt>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                {user.additional_info.attachments.find(a => a.document_type === 'PASSPORT')?.document ? (
                                                    <div className="flex flex-col gap-3">
                                                        <div className="group h-[200px] w-full max-w-md overflow-hidden rounded-lg bg-gray-100 border border-gray-200">
                                                            <ImageDisplay
                                                                s3Key={user.additional_info.attachments.find(a => a.document_type === 'PASSPORT').document.s3_key}
                                                                file=""
                                                                classStyle="object-contain w-full h-full group-hover:opacity-90 transition-opacity"
                                                            />
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm text-gray-900 font-medium">
                                                                {user.additional_info.attachments.find(a => a.document_type === 'PASSPORT').document.file_name}
                                                            </span>
                                                            <span className="text-xs text-gray-500">
                                                                ({(user.additional_info.attachments.find(a => a.document_type === 'PASSPORT').document.size / 1024 / 1024).toFixed(2)} MB)
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : '-'}
                                            </dd>
                                        </div>
                                    </dl>
                                </div>
                            </div>
                        )}

                        {/* National ID Details Section */}
                        {user.additional_info?.attachments?.find(a => a.document_type === 'NATIONAL_ID') && (
                            <div className="mt-8">
                                <div className="px-4 sm:px-0">
                                    <h3 className="text-lg font-semibold leading-7 text-primary">
                                        {t('National ID Details')}
                                    </h3>
                                    <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
                                        {t('National ID information of the user')}
                                    </p>
                                </div>
                                <div className="mt-3 border-t border-gray-100">
                                    <dl className="divide-y divide-gray-100">
                                        <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {t('National ID Number')}
                                            </dt>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                {user.additional_info.attachments.find(a => a.document_type === 'NATIONAL_ID')?.document_number || '-'}
                                            </dd>
                                        </div>
                                        <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {t('National ID Attachment')}
                                            </dt>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                {user.additional_info.attachments.find(a => a.document_type === 'NATIONAL_ID')?.document ? (
                                                    <div className="flex flex-col gap-3">
                                                        <div className="group h-[200px] w-full max-w-md overflow-hidden rounded-lg bg-gray-100 border border-gray-200">
                                                            <ImageDisplay
                                                                s3Key={user.additional_info.attachments.find(a => a.document_type === 'NATIONAL_ID').document.s3_key}
                                                                file=""
                                                                classStyle="object-contain w-full h-full group-hover:opacity-90 transition-opacity"
                                                            />
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm text-gray-900 font-medium">
                                                                {user.additional_info.attachments.find(a => a.document_type === 'NATIONAL_ID').document.file_name}
                                                            </span>
                                                            <span className="text-xs text-gray-500">
                                                                ({(user.additional_info.attachments.find(a => a.document_type === 'NATIONAL_ID').document.size / 1024 / 1024).toFixed(2)} MB)
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : '-'}
                                            </dd>
                                        </div>
                                    </dl>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title={t('Error')}
                    message={errorMessage}
                    onClose={handleErrClose}
                    isMentee={false}
                />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title={t('Success')} message={successMessage} onClose={handleSucClose} />
            )}
            {showConfirmDialog && (
                <ConfirmDialog
                    status={true}
                    title={t('User Status Update')}
                    negativeActionText={t('Cancel')}
                    positiveActionText={t('Yes')}
                    message={confirmMessage}
                    onPositive={handleConfirmPositive}
                    onNegative={handleConfirmNegative}
                />
            )}
            {statusUpdateLoading && <LoadingOverlay />}
        </>
    )
}
